import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const INTERVAL_MS = 5 * 60 * 1000; // periodic re-check every 5 minutes
const MIN_GAP_MS = 45 * 1000;      // debounce rapid route-change checks

const getCoords = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ location_denied: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({ location_denied: true }),
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }
    );
  });

// Continuous geo-fence guard. Re-validates device location on route changes and
// on a periodic interval; locks the UI full-screen when outside office bounds.
export const GeoGate = ({ children }) => {
  const { user } = useAuth();
  const exempt = !!user?.geofence_exempt;
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const lastRef = useRef(0);

  const runCheck = useCallback(async (force = false) => {
    if (!user || exempt) return;
    const now = Date.now();
    if (!force && now - lastRef.current < MIN_GAP_MS) return;
    lastRef.current = now;
    setChecking(true);
    try {
      const loc = await getCoords();
      const { data } = await api.post('/auth/geo-check', loc);
      setBlocked(!data.allowed);
    } catch {
      // Network/API error — don't hard-block on a transient failure.
    } finally {
      setChecking(false);
    }
  }, [user, exempt]);

  useEffect(() => { runCheck(false); }, [location.pathname, runCheck]);

  useEffect(() => {
    if (!user || exempt) return undefined;
    const t = setInterval(() => runCheck(true), INTERVAL_MS);
    return () => clearInterval(t);
  }, [user, exempt, runCheck]);

  if (blocked) {
    return (
      <div data-testid="geo-lock-overlay" className="fixed inset-0 z-[200] flex items-center justify-center bg-stone-900/70 p-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500"><MapPin className="h-7 w-7" /></div>
          <h1 className="font-head text-xl font-extrabold text-stone-900">Outside Office Location Boundaries</h1>
          <p className="mt-2 text-sm font-medium text-stone-500">
            Your device appears to be outside the authorized office geo-fence, or location access is unavailable.
            Move back within an office boundary and retry to continue.
          </p>
          <button data-testid="geo-retry-btn" onClick={() => runCheck(true)} disabled={checking}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-stone-800 disabled:opacity-60">
            {checking && <Loader2 className="h-4 w-4 animate-spin" />}Retry location check
          </button>
        </div>
      </div>
    );
  }
  return children;
};
